import React from 'react';
import { Form, Select, Space, Tooltip, Tag } from 'antd';
import './index.scss';
import delIcon from '../../../../../../assets/svg/del-content-icon.svg';
import addIcon from '../../../../../../assets/svg/group-add-icon.svg';
import classNames from 'classnames';
import { IAPIGameItem } from '../../../../../../model/api-modules';
import {
  IRBACRole,
  RbacRoleType,
  RbacRoleObjectKey,
} from '../../../../../../model/rbac';
import { formatEnvCollectionOptions } from '../../../../../game-content/game-filter/env';
import EnvDevIcon from '../../../../../../assets/img/env-dev-icon.png';
import EnvLiveIcon from '../../../../../../assets/img/env-live-icon.png';
import getFormatMessage from '../../../../../../locales/get-format-message';
import { PermissionCheck } from '../../../../../component/permission-check';
import { CustomPopover } from '../../../../../../components/custom-popover';

interface IProps {
  readonly rolesList: ReadonlyArray<IRBACRole>;
  readonly gameList: ReadonlyArray<IAPIGameItem>;
  readonly studioId: string;
  readonly allEnvList: any;
  readonly fields: Array<{
    role: number | null;
    game: string | 'all' | null;
    env: number[] | ['all'];
  }>;
  readonly setFields: (
    value: Array<{
      role: number | null;
      game: string | 'all' | null;
      env: number[] | ['all'];
    }>
  ) => void;
  readonly accessForm: any;
  readonly addRole: () => void;
  readonly disabled: boolean;
}

interface IOption {
  readonly label: string;
  readonly value: number | string;
  readonly type?: string;
}

const { Option, OptGroup } = Select;

export const AccessRights = (props: IProps) => {
  // const [form] = Form.useForm();
  const { fields, setFields } = props;

  const rolesOptions: IOption[] = [];
  props.rolesList.forEach((item) => {
    if (item.type !== RbacRoleType.Owner) {
      rolesOptions.push({
        label: item.roleName,
        value: item.roleId,
        type: item.type,
      });
    }
  });
  const gamesOptions: IOption[] = [];
  const games = props.gameList.filter(
    (v) => v.studioID === props.studioId && v.gameID
  );
  games.forEach((item) => {
    gamesOptions.push({
      label: item.gameName,
      value: item.gameID,
    });
  });

  const addField = () => {
    setFields([...fields, { role: null, game: null, env: [] }]);
  };

  const removeField = (index: number) => {
    if (fields.length === 1) {
      return;
    }
    setFields(fields.filter((_, i) => i !== index));
  };

  const updateField = (index: number, key: string, value: any) => {
    const newFields: any = [...fields];
    newFields[index][key] = value;
    if (key === 'game') {
      newFields[index].env = [];
      if (value === 'all') {
        newFields[index].env = ['all'];
      }
    }
    if (key === 'env' && value instanceof Array) {
      if ([...value].pop() === 'all') {
        newFields[index].env = ['all'];
      } else {
        newFields[index].env = value.filter((item) => item !== 'all');
      }
    }
    setFields(newFields);
  };

  const onSelectAll = (index: number, key: string, selectKey: string) => {
    const newFields: any = [...fields];
    if (selectKey === 'select') {
      if (key === 'game') {
        newFields[index].game = 'all';
      }
      newFields[index].env = ['all'];
    }
    if (selectKey === 'clean') {
      if (key === 'game') {
        newFields[index].game = null;
      }
      newFields[index].env = [];
    }
    setFields(newFields);
  };

  const tagRender = (itemProps: any) => {
    const { value, onClose, label } = itemProps;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    if (value === 'all') {
      return <div>{getFormatMessage('all_env')}</div>;
    }
    return (
      <Tag onMouseDown={onPreventMouseDown} onClose={onClose} closable={true}>
        {label}
      </Tag>
    );
  };

  return (
    <Form form={props.accessForm} layout="vertical" className="access-right">
      <div className="table-header" style={{ color: '#86909C' }}>
        <div style={{ width: '262px' }}>{getFormatMessage('role')}</div>
        <div style={{ width: '636px' }}>{getFormatMessage('scope')}</div>
        <div style={{ flex: 1, paddingLeft: '8px' }}>
          {getFormatMessage('operate')}
        </div>
      </div>
      {fields.map((field, index) => {
        const envs = field.game ? props.allEnvList[field.game] : [];
        const envOptions = formatEnvCollectionOptions(envs);
        const customRolesOptions = rolesOptions.filter(
          (item) => item.type === RbacRoleType.Custom
        );
        return (
          <Space
            key={index}
            className="table-space"
            style={{ display: 'flex', padding: '12px 16px' }}
            align="start"
          >
            <Form.Item style={{ marginRight: 8, marginBottom: 0 }}>
              {props.disabled ? (
                <CustomPopover
                  content={getFormatMessage('gourps_modified_tips')}
                >
                  <div>
                    <Select
                      placeholder={getFormatMessage('select_role')}
                      value={field.role}
                      onChange={(value) => updateField(index, 'role', value)}
                      style={{ width: '240px' }}
                      virtual={false}
                      popupClassName="access-right-role-box"
                      disabled
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <div
                            style={{
                              color: '#5764f1',
                              paddingLeft: '12px',
                              height: '32px',
                              lineHeight: '32px',
                              borderTop: '1px solid #F2F3F5',
                            }}
                          >
                            <PermissionCheck
                              permissionKey={RbacRoleObjectKey.RoleCreate}
                            >
                              <span
                                onClick={props.addRole}
                                style={{ cursor: 'pointer' }}
                              >
                                <img
                                  src={addIcon}
                                  style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    marginRight: '4px',
                                  }}
                                />
                                <span style={{ fontSize: '14px' }}>
                                  {getFormatMessage('add_new_role')}
                                </span>
                              </span>
                            </PermissionCheck>
                          </div>
                        </>
                      )}
                    >
                      <OptGroup
                        key="system"
                        label={getFormatMessage('access_right_system')}
                      >
                        {rolesOptions.map((role) => {
                          let roleName = role.label;
                          roleName =
                            roleName.slice(0, 1).toUpperCase() +
                            roleName.slice(1);
                          return role.type === RbacRoleType.Viewer ||
                            role.type === RbacRoleType.Editor ||
                            role.type === RbacRoleType.Admin ? (
                            <Option key={role.value} value={role.value}>
                              {roleName}
                            </Option>
                          ) : null;
                        })}
                      </OptGroup>
                      {customRolesOptions.length !== 0 && (
                        <OptGroup
                          key="custom"
                          label={getFormatMessage('access_right_custom')}
                        >
                          {customRolesOptions.map((role) => {
                            return (
                              <Option key={role.value} value={role.value}>
                                {role.label}
                              </Option>
                            );
                          })}
                        </OptGroup>
                      )}
                    </Select>
                  </div>
                </CustomPopover>
              ) : (
                <Select
                  placeholder={getFormatMessage('select_role')}
                  value={field.role}
                  onChange={(value) => updateField(index, 'role', value)}
                  style={{ width: '240px' }}
                  virtual={false}
                  popupClassName="access-right-role-box"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div
                        style={{
                          color: '#5764f1',
                          paddingLeft: '12px',
                          height: '32px',
                          lineHeight: '32px',
                          borderTop: '1px solid #F2F3F5',
                        }}
                      >
                        <PermissionCheck
                          permissionKey={RbacRoleObjectKey.RoleCreate}
                        >
                          <span
                            onClick={props.addRole}
                            style={{ cursor: 'pointer' }}
                          >
                            <img
                              src={addIcon}
                              style={{
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                marginRight: '4px',
                              }}
                            />
                            <span style={{ fontSize: '14px' }}>
                              {getFormatMessage('add_new_role')}
                            </span>
                          </span>
                        </PermissionCheck>
                      </div>
                    </>
                  )}
                >
                  <OptGroup
                    key="system"
                    label={getFormatMessage('access_right_system')}
                  >
                    {rolesOptions.map((role) => {
                      let roleName = role.label;
                      roleName =
                        roleName.slice(0, 1).toUpperCase() + roleName.slice(1);
                      return role.type === RbacRoleType.Viewer ||
                        role.type === RbacRoleType.Editor ? (
                        <Option key={role.value} value={role.value}>
                          {roleName}
                        </Option>
                      ) : null;
                    })}
                  </OptGroup>
                  {customRolesOptions.length !== 0 && (
                    <OptGroup
                      key="custom"
                      label={getFormatMessage('access_right_custom')}
                    >
                      {customRolesOptions.map((role) => {
                        return (
                          <Option key={role.value} value={role.value}>
                            {role.label}
                          </Option>
                        );
                      })}
                    </OptGroup>
                  )}
                </Select>
              )}
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              {props.disabled ? (
                <CustomPopover
                  content={getFormatMessage('gourps_modified_tips')}
                >
                  <div>
                    <Select
                      placeholder={getFormatMessage('select_game')}
                      value={field.game}
                      onChange={(value) => updateField(index, 'game', value)}
                      style={{ width: '200px' }}
                      className="group-select-title"
                      virtual={false}
                      disabled
                      dropdownRender={(menu) => (
                        <>
                          {gamesOptions.length !== 0 && (
                            <div
                              onClick={() =>
                                onSelectAll(
                                  index,
                                  'game',
                                  field.game === 'all' ? 'clean' : 'select'
                                )
                              }
                              style={{
                                color: '#5764f1',
                                cursor: 'pointer',
                                paddingLeft: '16px',
                                height: '40px',
                                lineHeight: '40px',
                              }}
                            >
                              {field.game === 'all'
                                ? getFormatMessage('access_right_clean_all')
                                : getFormatMessage('access_right_select_all')}
                            </div>
                          )}
                          {menu}
                        </>
                      )}
                    >
                      {gamesOptions.length !== 0 && (
                        <Option
                          key="all"
                          value="all"
                          style={{ display: 'none' }}
                        >
                          {getFormatMessage('all_game')}
                        </Option>
                      )}
                      {gamesOptions.map((game) => (
                        <Option
                          key={game.value}
                          value={game.value}
                          className="access-right-env-type-box"
                        >
                          {game.label}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </CustomPopover>
              ) : (
                <Select
                  placeholder={getFormatMessage('select_game')}
                  value={field.game}
                  onChange={(value) => updateField(index, 'game', value)}
                  style={{ width: '200px' }}
                  className="group-select-title"
                  virtual={false}
                  dropdownRender={(menu) => (
                    <>
                      {gamesOptions.length !== 0 && (
                        <div
                          onClick={() =>
                            onSelectAll(
                              index,
                              'game',
                              field.game === 'all' ? 'clean' : 'select'
                            )
                          }
                          style={{
                            color: '#5764f1',
                            cursor: 'pointer',
                            paddingLeft: '16px',
                            height: '40px',
                            lineHeight: '40px',
                          }}
                        >
                          {field.game === 'all'
                            ? getFormatMessage('access_right_clean_all')
                            : getFormatMessage('access_right_select_all')}
                        </div>
                      )}
                      {menu}
                    </>
                  )}
                >
                  {gamesOptions.length !== 0 && (
                    <Option key="all" value="all" style={{ display: 'none' }}>
                      {getFormatMessage('all_game')}
                    </Option>
                  )}
                  {gamesOptions.map((game) => (
                    <Option
                      key={game.value}
                      value={game.value}
                      className="access-right-env-type-box"
                    >
                      {game.label}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>

            <Form.Item style={{ marginLeft: -8, marginBottom: 0 }}>
              {props.disabled ? (
                <CustomPopover
                  content={getFormatMessage('gourps_modified_tips')}
                >
                  <div>
                    <Select
                      mode="multiple"
                      showSearch={false}
                      placeholder={getFormatMessage('select_environment')}
                      value={field.env}
                      onChange={(value) => updateField(index, 'env', value)}
                      style={{ width: '426px' }}
                      disabled
                      maxTagCount="responsive"
                      notFoundContent={getFormatMessage(
                        'access_right_select_game_tips'
                      )}
                      tagRender={tagRender}
                      className="group-select-env"
                      virtual={false}
                      dropdownRender={(menu) => (
                        <>
                          {(envOptions.length !== 0 ||
                            field.game === 'all') && (
                            <div
                              onClick={() =>
                                onSelectAll(
                                  index,
                                  'env',
                                  field.env.length !== 0 &&
                                    field.env[0] === 'all'
                                    ? 'clean'
                                    : 'select'
                                )
                              }
                              style={{
                                color: '#5764f1',
                                cursor: 'pointer',
                                paddingLeft: '16px',
                                height: '40px',
                                lineHeight: '40px',
                              }}
                            >
                              {field.env.length !== 0 && field.env[0] === 'all'
                                ? getFormatMessage('access_right_clean_all')
                                : getFormatMessage('access_right_select_all')}
                            </div>
                          )}
                          {menu}
                        </>
                      )}
                    >
                      {(envOptions.length !== 0 || field.game === 'all') && (
                        <Option
                          key="allEnv"
                          value="all"
                          style={{ display: 'none' }}
                        >
                          {getFormatMessage('all_env')}
                        </Option>
                      )}
                      {envOptions.map((env) => {
                        if (env.children) {
                          return env.children.map((child) => {
                            return (
                              <Option
                                key={child.envIncrementID}
                                value={child.envIncrementID}
                                className="access-right-env-type-box"
                              >
                                {child.envType === 'DEV' && (
                                  <img
                                    src={EnvDevIcon}
                                    className="access-right-env-type"
                                  />
                                )}
                                {child.envType === 'LIVE' && (
                                  <img
                                    src={EnvLiveIcon}
                                    className="access-right-env-type"
                                  />
                                )}
                                <span>{`${env.label} - ${child.envName}`}</span>
                              </Option>
                            );
                          });
                        }
                        return null;
                      })}
                    </Select>
                  </div>
                </CustomPopover>
              ) : (
                <Select
                  mode="multiple"
                  showSearch={false}
                  placeholder={getFormatMessage('select_environment')}
                  value={field.env}
                  onChange={(value) => updateField(index, 'env', value)}
                  style={{ width: '426px' }}
                  disabled={field.game === 'all'}
                  maxTagCount="responsive"
                  notFoundContent={getFormatMessage(
                    'access_right_select_game_tips'
                  )}
                  tagRender={tagRender}
                  className="group-select-env"
                  virtual={false}
                  dropdownRender={(menu) => (
                    <>
                      {(envOptions.length !== 0 || field.game === 'all') && (
                        <div
                          onClick={() =>
                            onSelectAll(
                              index,
                              'env',
                              field.env.length !== 0 && field.env[0] === 'all'
                                ? 'clean'
                                : 'select'
                            )
                          }
                          style={{
                            color: '#5764f1',
                            cursor: 'pointer',
                            paddingLeft: '16px',
                            height: '40px',
                            lineHeight: '40px',
                          }}
                        >
                          {field.env.length !== 0 && field.env[0] === 'all'
                            ? getFormatMessage('access_right_clean_all')
                            : getFormatMessage('access_right_select_all')}
                        </div>
                      )}
                      {menu}
                    </>
                  )}
                >
                  {(envOptions.length !== 0 || field.game === 'all') && (
                    <Option
                      key="allEnv"
                      value="all"
                      style={{ display: 'none' }}
                    >
                      {getFormatMessage('all_env')}
                    </Option>
                  )}
                  {envOptions.map((env) => {
                    if (env.children) {
                      return env.children.map((child) => {
                        return (
                          <Option
                            key={child.envIncrementID}
                            value={child.envIncrementID}
                            className="access-right-env-type-box"
                          >
                            {child.envType === 'DEV' && (
                              <img
                                src={EnvDevIcon}
                                className="access-right-env-type"
                              />
                            )}
                            {child.envType === 'LIVE' && (
                              <img
                                src={EnvLiveIcon}
                                className="access-right-env-type"
                              />
                            )}
                            <span>{`${env.label} - ${child.envName}`}</span>
                          </Option>
                        );
                      });
                    }
                    return null;
                  })}
                </Select>
              )}
            </Form.Item>

            {fields.length === 1 || props.disabled ? (
              <Tooltip
                title={
                  props.disabled
                    ? getFormatMessage('gourps_modified_tips')
                    : getFormatMessage('access_right_delete_tips')
                }
                placement="topRight"
                overlayClassName="access-right-tooltip"
              >
                <img
                  src={delIcon}
                  className={classNames('del-icon', 'del-icon-disabled')}
                />
              </Tooltip>
            ) : (
              <img
                src={delIcon}
                onClick={() => removeField(index)}
                className="del-icon"
              />
            )}
          </Space>
        );
      })}

      <Form.Item style={{ height: '40px', marginBottom: 0, paddingTop: '4px' }}>
        {props.disabled ? (
          <CustomPopover content={getFormatMessage('gourps_modified_tips')}>
            <span className="add-item" style={{ opacity: 0.5 }}>
              <img src={addIcon} />
              <span>{getFormatMessage('add_role_and_scope')}</span>
            </span>
          </CustomPopover>
        ) : (
          <span onClick={addField} className="add-item">
            <img src={addIcon} />
            <span>{getFormatMessage('add_role_and_scope')}</span>
          </span>
        )}
      </Form.Item>
    </Form>
  );
};
